import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Card,
  Tabs,
  Select,
  DatePicker,
  Switch,
  InputNumber,
  Descriptions,
  Row,
  Col,
  Statistic,
  Tooltip,
  Drawer,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  RedoOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  agentWrokflowService,
  AgentWrokflowMessage,
  AgentWrokflowExecution,
  AgentWrokflowNode,
  AgentWrokflowRetry,
  AgentWrokflowManualReview,
} from '../services/agentWrokflowService';
import { useI18n } from '../hooks/useI18n';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

type TabKey = 'messages' | 'executions' | 'nodes' | 'retries' | 'manual-reviews';

const AgentWrokflowManager: React.FC = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabKey>('messages');
  const [loading, setLoading] = useState(false);
  
  // 数据状态
  const [messages, setMessages] = useState<AgentWrokflowMessage[]>([]);
  const [executions, setExecutions] = useState<AgentWrokflowExecution[]>([]);
  const [nodes, setNodes] = useState<AgentWrokflowNode[]>([]);
  const [retries, setRetries] = useState<AgentWrokflowRetry[]>([]);
  const [manualReviews, setManualReviews] = useState<AgentWrokflowManualReview[]>([]);
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [detailItem, setDetailItem] = useState<any>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  
  // 筛选状态
  const [filters, setFilters] = useState<any>({});
  
  // 统计信息
  const [statistics, setStatistics] = useState({
    totalMessages: 0,
    unprocessedMessages: 0,
    totalExecutions: 0,
    runningExecutions: 0,
    failedExecutions: 0,
    pendingReviews: 0,
  });

  const currentTenant = localStorage.getItem('currentTenant') || 'default';

  useEffect(() => {
    loadData();
    loadStatistics();
  }, [activeTab, pagination.current, pagination.pageSize, filters]);

  // 监听租户变化
  useEffect(() => {
    const handleTenantChange = () => {
      loadData();
      loadStatistics();
    };
    window.addEventListener('storage', handleTenantChange);
    window.addEventListener('tenantChanged', handleTenantChange);
    return () => {
      window.removeEventListener('storage', handleTenantChange);
      window.removeEventListener('tenantChanged', handleTenantChange);
    };
  }, []);

  // 当选择执行记录时，加载节点和重试记录
  useEffect(() => {
    if (selectedExecutionId && (activeTab === 'nodes' || activeTab === 'retries')) {
      loadNodesOrRetries();
    }
  }, [selectedExecutionId, activeTab]);

  const loadStatistics = async () => {
    try {
      // 加载消息统计
      const messagesData = await agentWrokflowService.queryMessages({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
      });
      const unprocessedMessagesData = await agentWrokflowService.queryMessages({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
        filters: { is_processed: false },
      });

      // 加载执行统计
      const executionsData = await agentWrokflowService.queryExecutions({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
      });
      const runningExecutionsData = await agentWrokflowService.queryExecutions({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
        filters: { status: 'running' },
      });
      const failedExecutionsData = await agentWrokflowService.queryExecutions({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
        filters: { status: 'failed' },
      });

      // 加载审核统计
      const reviewsData = await agentWrokflowService.queryManualReviews({
        tenant_code: currentTenant,
        page: 1,
        page_size: 1,
        filters: { review_status: 'pending' },
      });

      setStatistics({
        totalMessages: messagesData.total,
        unprocessedMessages: unprocessedMessagesData.total,
        totalExecutions: executionsData.total,
        runningExecutions: runningExecutionsData.total,
        failedExecutions: failedExecutionsData.total,
        pendingReviews: reviewsData.total,
      });
    } catch (error) {
      console.error('加载统计信息失败:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        tenant_code: currentTenant,
        page: pagination.current,
        page_size: pagination.pageSize,
        filters: filters[activeTab] || {},
      };

      switch (activeTab) {
        case 'messages':
          const messagesData = await agentWrokflowService.queryMessages(params);
          setMessages(messagesData.data);
          setPagination(prev => ({ ...prev, total: messagesData.total }));
          break;
        case 'executions':
          const executionsData = await agentWrokflowService.queryExecutions(params);
          setExecutions(executionsData.data);
          setPagination(prev => ({ ...prev, total: executionsData.total }));
          break;
        case 'nodes':
          // 节点需要通过执行ID获取
          if (selectedExecutionId) {
            const nodesData = await agentWrokflowService.getNodes(selectedExecutionId);
            setNodes(nodesData);
            setPagination(prev => ({ ...prev, total: nodesData.length }));
          } else {
            setNodes([]);
            setPagination(prev => ({ ...prev, total: 0 }));
          }
          break;
        case 'retries':
          // 重试记录需要通过执行ID获取
          if (selectedExecutionId) {
            const retriesData = await agentWrokflowService.getRetries(selectedExecutionId);
            setRetries(retriesData);
            setPagination(prev => ({ ...prev, total: retriesData.length }));
          } else {
            setRetries([]);
            setPagination(prev => ({ ...prev, total: 0 }));
          }
          break;
        case 'manual-reviews':
          const reviewsData = await agentWrokflowService.queryManualReviews(params);
          setManualReviews(reviewsData.data);
          setPagination(prev => ({ ...prev, total: reviewsData.total }));
          break;
      }
    } catch (error: any) {
      console.error('加载数据失败:', error);
      message.error(error.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadNodesOrRetries = async () => {
    if (!selectedExecutionId) return;
    setLoading(true);
    try {
      if (activeTab === 'nodes') {
        const nodesData = await agentWrokflowService.getNodes(selectedExecutionId);
        setNodes(nodesData);
        setPagination(prev => ({ ...prev, total: nodesData.length }));
      } else if (activeTab === 'retries') {
        const retriesData = await agentWrokflowService.getRetries(selectedExecutionId);
        setRetries(retriesData);
        setPagination(prev => ({ ...prev, total: retriesData.length }));
      }
    } catch (error: any) {
      console.error('加载数据失败:', error);
      message.error(error.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setEditingItem(record);
    // 处理 message_data、message_content 和 attachments_info：如果是对象，转换为 JSON 字符串
    const formValues = { ...record };
    if (formValues.message_data && typeof formValues.message_data === 'object') {
      formValues.message_data = JSON.stringify(formValues.message_data, null, 2);
    }
    if (formValues.message_content && typeof formValues.message_content === 'object') {
      formValues.message_content = JSON.stringify(formValues.message_content, null, 2);
    }
    if (formValues.attachments_info && typeof formValues.attachments_info === 'object') {
      formValues.attachments_info = JSON.stringify(formValues.attachments_info, null, 2);
    }
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = async (record: any) => {
    try {
      let detail: any;
      switch (activeTab) {
        case 'messages':
          detail = await agentWrokflowService.getMessage(record.message_id);
          break;
        case 'executions':
          detail = await agentWrokflowService.getExecution(record.execution_id);
          break;
        case 'manual-reviews':
          detail = await agentWrokflowService.getManualReview(record.id!);
          break;
        default:
          detail = record;
      }
      setDetailItem(detail);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error('获取详情失败: ' + error.message);
    }
  };

  const handleDelete = async (record: any) => {
    try {
      switch (activeTab) {
        case 'messages':
          await agentWrokflowService.deleteMessage(record.message_id);
          break;
        case 'executions':
          await agentWrokflowService.deleteExecution(record.execution_id);
          break;
        case 'manual-reviews':
          await agentWrokflowService.deleteManualReview(record.id!);
          break;
        case 'nodes':
        case 'retries':
          // 节点和重试记录不允许删除
          message.warning('节点和重试记录不允许删除');
          return;
      }
      message.success('删除成功');
      loadData();
      loadStatistics();
    } catch (error: any) {
      message.error('删除失败: ' + error.message);
    }
  };

  const handleExecute = async (record: AgentWrokflowMessage) => {
    try {
      const result = await agentWrokflowService.executeWorkflow(record.message_id);
      message.success(`工作流执行已启动，执行ID: ${result.execution_id}`);
      // 更新消息状态为已处理
      await agentWrokflowService.updateMessage(record.message_id, { is_processed: true });
      loadData();
      loadStatistics();
    } catch (error: any) {
      message.error('执行失败: ' + error.message);
    }
  };

  const handleModalSubmit = async (values: any) => {
    try {
      switch (activeTab) {
        case 'messages':
          // 处理 message_data、message_content 和 attachments_info：如果是 JSON 字符串，解析为对象
          const processedValues = { ...values };
          if (processedValues.message_data && typeof processedValues.message_data === 'string') {
            try {
              processedValues.message_data = JSON.parse(processedValues.message_data);
            } catch (e) {
              // 如果不是有效的 JSON，保持原字符串
            }
          }
          if (processedValues.message_content && typeof processedValues.message_content === 'string') {
            try {
              processedValues.message_content = JSON.parse(processedValues.message_content);
            } catch (e) {
              // 如果不是有效的 JSON，保持原字符串
            }
          }
          if (processedValues.attachments_info && typeof processedValues.attachments_info === 'string') {
            try {
              processedValues.attachments_info = JSON.parse(processedValues.attachments_info);
            } catch (e) {
              // 如果不是有效的 JSON，保持原字符串
            }
          }
          if (editingItem) {
            await agentWrokflowService.updateMessage(editingItem.message_id, processedValues);
          }
          break;
        case 'executions':
          if (editingItem) {
            await agentWrokflowService.updateExecution(editingItem.execution_id, values);
          }
          break;
        case 'manual-reviews':
          if (editingItem) {
            await agentWrokflowService.updateManualReview(editingItem.id!, values);
          }
          break;
      }
      message.success(editingItem ? '更新成功' : '创建成功');
      setModalVisible(false);
      setEditingItem(null);
      form.resetFields();
      loadData();
      loadStatistics();
    } catch (error: any) {
      message.error('操作失败: ' + error.message);
    }
  };

  const handleFilterSubmit = (values: any) => {
    const newFilters = { ...filters };
    newFilters[activeTab] = values;
    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    setFilterDrawerVisible(false);
  };

  const handleResetFilters = () => {
    const newFilters = { ...filters };
    newFilters[activeTab] = {};
    setFilters(newFilters);
    filterForm.resetFields();
    setPagination({ ...pagination, current: 1 });
  };

  // 消息表格列
  const messageColumns = [
    {
      title: '消息ID',
      dataIndex: 'message_id',
      key: 'message_id',
      width: 200,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '来源渠道',
      dataIndex: 'source_channel',
      key: 'source_channel',
      width: 120,
      render: (channel: string) => (
        <Tag color="blue">{channel}</Tag>
      ),
    },
    {
      title: '发送者',
      dataIndex: 'sender_identifier',
      key: 'sender_identifier',
      width: 150,
      ellipsis: true,
    },
    {
      title: '主题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Agent名称',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Agent用户ID',
      dataIndex: 'agent_user_id',
      key: 'agent_user_id',
      width: 150,
      ellipsis: true,
      render: (text: string) => text ? <Text code style={{ fontSize: '12px' }}>{text}</Text> : '-',
    },
    {
      title: '是否处理',
      dataIndex: 'is_processed',
      key: 'is_processed',
      width: 100,
      render: (processed: boolean) => (
        <Tag color={processed ? 'green' : 'orange'}>
          {processed ? '已处理' : '未处理'}
        </Tag>
      ),
    },
    {
      title: '接收时间',
      dataIndex: 'received_at',
      key: 'received_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: AgentWrokflowMessage) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {!record.is_processed && (
            <Popconfirm
              title="确定要执行这条消息的工作流吗？"
              onConfirm={() => handleExecute(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                icon={<ThunderboltOutlined />}
                style={{ color: '#1890ff' }}
              >
                执行
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定要删除这条消息吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 执行表格列
  const executionColumns = [
    {
      title: '执行ID',
      dataIndex: 'execution_id',
      key: 'execution_id',
      width: 200,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '工作流名称',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
      width: 150,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; icon: any }> = {
          pending: { color: 'default', icon: <ClockCircleOutlined /> },
          running: { color: 'processing', icon: <ReloadOutlined spin /> },
          success: { color: 'success', icon: <CheckCircleOutlined /> },
          failed: { color: 'error', icon: <CloseCircleOutlined /> },
          retrying: { color: 'warning', icon: <RedoOutlined /> },
          manual_review: { color: 'orange', icon: <ClockCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: '当前节点',
      dataIndex: 'current_node',
      key: 'current_node',
      width: 150,
    },
    {
      title: '重试次数',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 100,
      render: (count: number, record: AgentWrokflowExecution) => (
        <Text>{count} / {record.max_retries}</Text>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: AgentWrokflowExecution) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Tooltip title="查看节点和重试记录">
            <Button
              type="link"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => {
                setSelectedExecutionId(record.execution_id);
                if (activeTab !== 'nodes' && activeTab !== 'retries') {
                  setActiveTab('nodes');
                } else {
                  loadNodesOrRetries();
                }
              }}
            >
              节点
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这条执行记录吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 节点表格列
  const nodeColumns = [
    {
      title: '节点名称',
      dataIndex: 'node_name',
      key: 'node_name',
      width: 150,
    },
    {
      title: '节点类型',
      dataIndex: 'node_type',
      key: 'node_type',
      width: 120,
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: '顺序',
      dataIndex: 'node_order',
      key: 'node_order',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig: Record<string, string> = {
          pending: 'default',
          running: 'processing',
          success: 'success',
          failed: 'error',
          skipped: 'warning',
        };
        return <Tag color={statusConfig[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: '重试次数',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 100,
    },
    {
      title: '执行时间(ms)',
      dataIndex: 'execution_time_ms',
      key: 'execution_time_ms',
      width: 120,
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: AgentWrokflowNode) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setDetailItem(record);
            setDetailModalVisible(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  // 重试表格列
  const retryColumns = [
    {
      title: '重试序号',
      dataIndex: 'retry_number',
      key: 'retry_number',
      width: 100,
    },
    {
      title: '重试原因',
      dataIndex: 'retry_reason',
      key: 'retry_reason',
      width: 150,
      render: (reason: string) => <Tag color="orange">{reason}</Tag>,
    },
    {
      title: '重试时间',
      dataIndex: 'retry_at',
      key: 'retry_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '重试结果',
      dataIndex: 'retry_result',
      key: 'retry_result',
      width: 100,
      render: (result: string) => (
        <Tag color={result === 'success' ? 'green' : 'red'}>
          {result === 'success' ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '错误消息',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: AgentWrokflowRetry) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setDetailItem(record);
            setDetailModalVisible(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  // 人工审核表格列
  const manualReviewColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '执行ID',
      dataIndex: 'execution_id',
      key: 'execution_id',
      width: 200,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '审核状态',
      dataIndex: 'review_status',
      key: 'review_status',
      width: 120,
      render: (status: string) => {
        const statusConfig: Record<string, string> = {
          pending: 'default',
          reviewing: 'processing',
          resolved: 'success',
          rejected: 'error',
        };
        return <Tag color={statusConfig[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: '审核原因',
      dataIndex: 'review_reason',
      key: 'review_reason',
      width: 150,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: AgentWrokflowManualReview) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条审核记录吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'messages':
        return messages;
      case 'executions':
        return executions;
      case 'nodes':
        return nodes;
      case 'retries':
        return retries;
      case 'manual-reviews':
        return manualReviews;
      default:
        return [];
    }
  };

  const getCurrentColumns = (): any[] => {
    switch (activeTab) {
      case 'messages':
        return messageColumns;
      case 'executions':
        return executionColumns;
      case 'nodes':
        return nodeColumns;
      case 'retries':
        return retryColumns;
      case 'manual-reviews':
        return manualReviewColumns;
      default:
        return [];
    }
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'messages':
        return (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="source_channel" label="来源渠道">
                  <Select>
                    <Option value="email">Email</Option>
                    <Option value="whatsapp">WhatsApp</Option>
                    <Option value="wechat">WeChat</Option>
                    <Option value="api">API</Option>
                    <Option value="webhook">Webhook</Option>
                    <Option value="sms">SMS</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="is_processed" label="是否处理" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="sender_identifier" label="发送者">
                  <Input size="small" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="receiver_identifier" label="接收者">
                  <Input size="small" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="subject" label="主题">
              <Input size="small" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="agent_name" label="Agent名称">
                  <Input size="small" placeholder="如：WhatsAppOrderAgent" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="agent_user_id" label="Agent用户ID">
                  <Input size="small" placeholder="如：agent_user_xxx" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="message_data" label="Message Data (JSON)">
              <TextArea
                rows={6}
                placeholder='请输入 JSON 格式的数据，例如: {"key": "value"}'
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </Form.Item>
            <Form.Item name="message_content" label="Message Content">
              <TextArea
                rows={6}
                placeholder="请输入消息内容"
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </Form.Item>
            <Form.Item name="attachments_info" label="Attachments Info (JSON)">
              <TextArea
                rows={6}
                placeholder='请输入 JSON 格式的附件信息，例如: [{"filename": "file.pdf", "size": 1024}]'
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </Form.Item>
          </>
        );
      case 'executions':
        return (
          <>
            <Form.Item name="workflow_name" label="工作流名称">
              <Input />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="running">Running</Option>
                <Option value="success">Success</Option>
                <Option value="failed">Failed</Option>
                <Option value="retrying">Retrying</Option>
                <Option value="manual_review">Manual Review</Option>
              </Select>
            </Form.Item>
            <Form.Item name="current_node" label="当前节点">
              <Input />
            </Form.Item>
            <Form.Item name="max_retries" label="最大重试次数">
              <InputNumber min={0} max={10} />
            </Form.Item>
            <Form.Item name="error_message" label="错误消息">
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item name="error_code" label="错误代码">
              <Input />
            </Form.Item>
          </>
        );
      case 'manual-reviews':
        return (
          <>
            <Form.Item name="review_status" label="审核状态">
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="reviewing">Reviewing</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Form.Item>
            <Form.Item name="review_reason" label="审核原因">
              <Select>
                <Option value="missing_customer">Missing Customer</Option>
                <Option value="invalid_params">Invalid Params</Option>
                <Option value="api_failed">API Failed</Option>
                <Option value="parse_error">Parse Error</Option>
                <Option value="timeout">Timeout</Option>
                <Option value="unknown">Unknown</Option>
              </Select>
            </Form.Item>
            <Form.Item name="review_notes" label="审核备注">
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item name="resolution_action" label="解决方案">
              <Select>
                <Option value="retry">Retry</Option>
                <Option value="ignore">Ignore</Option>
                <Option value="cancel">Cancel</Option>
              </Select>
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  const renderFilterFields = () => {
    switch (activeTab) {
      case 'messages':
        return (
          <>
            <Form.Item name="source_channel" label="来源渠道">
              <Select allowClear>
                <Option value="email">Email</Option>
                <Option value="whatsapp">WhatsApp</Option>
                <Option value="wechat">WeChat</Option>
                <Option value="api">API</Option>
              </Select>
            </Form.Item>
            <Form.Item name="is_processed" label="是否处理">
              <Select allowClear>
                <Option value={true}>已处理</Option>
                <Option value={false}>未处理</Option>
              </Select>
            </Form.Item>
            <Form.Item name="date_range" label="接收时间">
              <RangePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </>
        );
      case 'executions':
        return (
          <>
            <Form.Item name="status" label="状态">
              <Select allowClear>
                <Option value="pending">Pending</Option>
                <Option value="running">Running</Option>
                <Option value="success">Success</Option>
                <Option value="failed">Failed</Option>
                <Option value="retrying">Retrying</Option>
                <Option value="manual_review">Manual Review</Option>
              </Select>
            </Form.Item>
            <Form.Item name="source_channel" label="来源渠道">
              <Select allowClear>
                <Option value="email">Email</Option>
                <Option value="whatsapp">WhatsApp</Option>
                <Option value="wechat">WeChat</Option>
                <Option value="api">API</Option>
              </Select>
            </Form.Item>
            <Form.Item name="workflow_name" label="工作流名称">
              <Input allowClear />
            </Form.Item>
            <Form.Item name="date_range" label="开始时间">
              <RangePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </>
        );
      case 'manual-reviews':
        return (
          <>
            <Form.Item name="review_status" label="审核状态">
              <Select allowClear>
                <Option value="pending">Pending</Option>
                <Option value="reviewing">Reviewing</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Form.Item>
            <Form.Item name="review_reason" label="审核原因">
              <Select allowClear>
                <Option value="missing_customer">Missing Customer</Option>
                <Option value="invalid_params">Invalid Params</Option>
                <Option value="api_failed">API Failed</Option>
                <Option value="parse_error">Parse Error</Option>
                <Option value="timeout">Timeout</Option>
                <Option value="unknown">Unknown</Option>
              </Select>
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>工作流管理</Title>
        
        {/* 统计信息 */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总消息数"
                value={statistics.totalMessages}
                prefix={<ReloadOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="未处理消息"
                value={statistics.unprocessedMessages}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总执行数"
                value={statistics.totalExecutions}
                prefix={<PlayCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="失败执行"
                value={statistics.failedExecutions}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
        
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as TabKey);
            setPagination({ current: 1, pageSize: 20, total: 0 });
            setFilters({});
            setSelectedExecutionId(null);
          }}
          items={[
            {
              key: 'messages',
              label: '消息 (Messages)',
            },
            {
              key: 'executions',
              label: '执行记录 (Executions)',
            },
            {
              key: 'nodes',
              label: `节点 (Nodes)${selectedExecutionId ? ` - ${selectedExecutionId.substring(0, 20)}...` : ''}`,
            },
            {
              key: 'retries',
              label: `重试记录 (Retries)${selectedExecutionId ? ` - ${selectedExecutionId.substring(0, 20)}...` : ''}`,
            },
            {
              key: 'manual-reviews',
              label: '人工审核 (Manual Reviews)',
            },
          ]}
        />

        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadData();
                loadStatistics();
              }}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                filterForm.setFieldsValue(filters[activeTab] || {});
                setFilterDrawerVisible(true);
              }}
            >
              筛选
            </Button>
            {(activeTab === 'messages' || activeTab === 'executions' || activeTab === 'manual-reviews') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingItem(null);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                新建
              </Button>
            )}
            {selectedExecutionId && (activeTab === 'nodes' || activeTab === 'retries') && (
              <Button
                onClick={() => {
                  setSelectedExecutionId(null);
                  setNodes([]);
                  setRetries([]);
                }}
              >
                返回执行列表
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={getCurrentColumns() as any}
          dataSource={getCurrentData() as any}
          rowKey={(record: any) => {
            switch (activeTab) {
              case 'messages':
                return record.message_id;
              case 'executions':
                return record.execution_id;
              case 'nodes':
                return record.id || record.node_name;
              case 'retries':
                return record.id || record.retry_number;
              case 'manual-reviews':
                return record.id;
              default:
                return record.id || Math.random();
            }
          }}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* 编辑/创建模态框 */}
      <Modal
        title={editingItem ? '编辑' : '新建'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={activeTab === 'messages' ? 700 : 600}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          {renderFormFields()}
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setDetailItem(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={activeTab === 'messages' ? 800 : 800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {detailItem && (
          <>
            {activeTab === 'messages' ? (
              // 消息详情：message_data、message_content 和 attachments_info 用大区块显示
              <div>
                <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                  {Object.entries(detailItem)
                    .filter(([key]) => key !== 'message_data' && key !== 'message_content' && key !== 'attachments_info' && key !== 'metadata')
                    .sort(([keyA], [keyB]) => {
                      // 重要字段优先显示
                      const priority = ['message_id', 'source_channel', 'agent_name', 'agent_user_id', 'sender_identifier', 'receiver_identifier', 'subject'];
                      const indexA = priority.indexOf(keyA);
                      const indexB = priority.indexOf(keyB);
                      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                      if (indexA !== -1) return -1;
                      if (indexB !== -1) return 1;
                      return keyA.localeCompare(keyB);
                    })
                    .map(([key, value]) => (
                      <Descriptions.Item 
                        key={key} 
                        label={key === 'agent_name' ? 'Agent名称' : key === 'agent_user_id' ? 'Agent用户ID' : key}
                        span={key === 'message_id' || key === 'source_channel' || key === 'agent_name' || key === 'agent_user_id' ? 1 : 2}
                      >
                        {typeof value === 'object' && value !== null ? (
                          <pre style={{ margin: 0, maxHeight: '100px', overflow: 'auto', fontSize: '12px' }}>
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : (
                          <span style={{ fontSize: '12px' }}>
                            {key === 'agent_user_id' && value ? (
                              <Text code style={{ fontSize: '12px' }}>{String(value)}</Text>
                            ) : (
                              String(value || '-')
                            )}
                          </span>
                        )}
                      </Descriptions.Item>
                    ))}
                </Descriptions>
                {(detailItem.message_data || detailItem.message_content || detailItem.attachments_info) && (
                  <div style={{ marginTop: 16 }}>
                    {detailItem.message_data && (
                      <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Message Data:</Text>
                        <TextArea
                          value={typeof detailItem.message_data === 'object' 
                            ? JSON.stringify(detailItem.message_data, null, 2)
                            : String(detailItem.message_data || '')}
                          rows={6}
                          readOnly
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                    {detailItem.message_content && (
                      <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Message Content:</Text>
                        <TextArea
                          value={typeof detailItem.message_content === 'object'
                            ? JSON.stringify(detailItem.message_content, null, 2)
                            : String(detailItem.message_content || '')}
                          rows={6}
                          readOnly
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                    {detailItem.attachments_info && (
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Attachments Info:</Text>
                        <TextArea
                          value={typeof detailItem.attachments_info === 'object'
                            ? JSON.stringify(detailItem.attachments_info, null, 2)
                            : String(detailItem.attachments_info || '')}
                          rows={6}
                          readOnly
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // 其他类型的详情：保持原样
              <Descriptions column={2} bordered>
                {Object.entries(detailItem).map(([key, value]) => (
                  <Descriptions.Item key={key} label={key}>
                    {typeof value === 'object' && value !== null ? (
                      <pre style={{ margin: 0, maxHeight: '200px', overflow: 'auto' }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      String(value || '-')
                    )}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </>
        )}
      </Modal>

      {/* 筛选抽屉 */}
      <Drawer
        title="筛选条件"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={400}
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleFilterSubmit}
          initialValues={filters[activeTab] || {}}
        >
          {renderFilterFields()}
          <Divider />
          <Space>
            <Button type="primary" htmlType="submit">
              应用筛选
            </Button>
            <Button onClick={handleResetFilters}>
              重置
            </Button>
            <Button onClick={() => setFilterDrawerVisible(false)}>
              取消
            </Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default AgentWrokflowManager;
